import { Button } from '@/components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { useAuthActions } from '@convex-dev/auth/react';
import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface OTPPageProps {
  email: string;
  onBack: () => void;
}

export const OTPPage = ({ email, onBack }: OTPPageProps) => {
  const { t } = useTranslation();
  const { signIn } = useAuthActions();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.set('code', code);
      formData.set('email', email);
      await signIn('resend-otp', formData);
    } catch {
      setError(t('errors:invalidCode'));
      setCode('');
      setIsLoading(false);
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: 'easeOut' as const,
      },
    },
  };

  return (
    <>
      <motion.div
        className="flex flex-col items-center justify-center gap-2"
        variants={itemVariants}>
        <h1 className="text-center text-3xl font-bold">
          {t('login:otpTitle')}
        </h1>
        <p className="text-muted-foreground text-center text-sm">
          {t('login:otpSubtitle')}
        </p>
      </motion.div>

      <motion.form
        ref={formRef}
        onSubmit={handleSubmit}
        className="flex w-full flex-col items-center justify-center gap-4"
        variants={itemVariants}>
        <InputOTP
          containerClassName="w-full justify-between"
          maxLength={6}
          value={code}
          onChange={setCode}
          onComplete={() => {
            if (formRef.current) {
              formRef.current.requestSubmit();
            }
          }}
          disabled={isLoading}>
          <InputOTPGroup className="w-full justify-between">
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>

        {error && <p className="text-destructive w-full text-sm">{error}</p>}

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading || code.length !== 6}>
          {isLoading ? t('login:verifying') : t('login:otpContinue')}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onBack}
          disabled={isLoading}>
          {t('login:back')}
        </Button>
      </motion.form>
    </>
  );
};
